import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { ObjectResponse } from '@verantos-dev/types-common';

import { authFetch } from './authFetch';
import { projectPageKeys } from './queryKeys';

/** Just the two ids the artifacts page needs to route into the new dataset. */
export interface CreatedAnalyticalDataset {
  analyticalDatasetProfileId: string;
  latestAnalyticalDatasetVersionId: string | null;
}

interface CreateAnalyticalDatasetInput {
  projectId: string;
  name: string;
}

/**
 * Create an analytical-dataset profile from the artifacts page. Duplicates the
 * POST in feature-analytical-datasets on purpose — that lib can't be imported
 * here (dependency cycle). Promote to a shared hook if the shapes diverge.
 */
export const useCreateAnalyticalDatasetMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, name }: CreateAnalyticalDatasetInput) =>
      authFetch<ObjectResponse<CreatedAnalyticalDataset>>(
        `/analytical-datasets/${encodeURIComponent(projectId)}/analytical-dataset-profile`,
        {
          method: 'POST',
          body: JSON.stringify({ name }),
        }
      ),
    onSuccess: (_data, variables) => {
      // The new dataset materializes into PROJECT_ARTIFACT server-side, so
      // refresh the manifest the artifacts list reads from.
      queryClient.invalidateQueries({
        queryKey: projectPageKeys.artifactsForProject(variables.projectId),
      });
    },
  });
};
